<script setup lang="ts">
import { watch, computed } from 'vue'
import { useRefiner } from '@v3c/composables/useRefiner'

import { mdiCommentQuote } from '@mdi/js'

const props = defineProps<{
  projectId: string
  formId: string
  userId?: string
  userEmail?: string
  userName?: string
  locale?: string
  userRoles?: string[]
  marketCountryCode?: string
  locationNumber?: string
  appVersion?: string
  route?: string
  isMobile?: boolean
}>()


const effectiveLocale = computed(() => props.locale || 'nl')
// TODO: for now 'nl' since Refiner uses this as default, we'd like to make this 'en' in the future

const hasUser = computed(() => !!props.userId || !!props.userEmail)

const { setLocale, identifyCurrentUser, showRefinerForm, trackEvent } = useRefiner({
  projectId: props.projectId,
  locale: effectiveLocale.value,
  user: hasUser.value
    ? {
        userId: props.userId,
        userEmail: props.userEmail,
        userName: props.userName,
        locale: props.locale,
      }
    : undefined, // anonymous feedback if no user
})

// Reactively update locale in Refiner if user changes locale
watch(effectiveLocale, (newLocale) => {
  setLocale(newLocale)
})

// Reactively identify user if info changes after mount
watch(
  () => [props.userId, props.userEmail, props.userName, props.locale],
  ([newUserId, newUserEmail, newUserName, newLocale]) => {
    if (newUserId || newUserEmail) {
      identifyCurrentUser({
        userId: newUserId,
        userEmail: newUserEmail,
        userName: newUserName,
        locale: newLocale,
      })
    }
  }
)

const onClick = () => {
  trackEvent('feedback_button_clicked', {
    route: props.route,
    isMobile: props.isMobile,
  })

  showRefinerForm({
    formId: props.formId,
    locale: effectiveLocale.value,
    user: hasUser.value
      ? {
          userId: props.userId,
          userEmail: props.userEmail,
          userName: props.userName,
          locale: props.locale,
        }
      : undefined,
    userRoles: props.userRoles,
    appVersion: props.appVersion,
    marketCountryCode: props.marketCountryCode,
    locationNumber: props.locationNumber, // can be multiple selected ???
    route: props.route,
    isMobile: props.isMobile,
  })
}
</script>

<template>
  <!-- Mobile view -->
  <div
    v-if="isMobile"
    class="d-flex mx-3 align-center crv-refiner-button crv-app-nav-item"
    @click="onClick"
  >
    <v-icon
      :icon="mdiCommentQuote"
      class="mr-4"
      size="small"
    />
    <span class="d-flex align-center crv-refiner-label crv-app-nav-item_label">Feedback</span>
  </div>

  <!-- Desktop view -->
  <v-list-item
    v-else
    title="Feedback"
    :prepend-icon="mdiCommentQuote"
    @click="onClick"
  />
</template>

<style lang="scss" scoped>
@use '@v3c/assets/styles/variables.scss' as *;

.crv-app-nav-item {
  height: $element-height-4;
}
</style>